import type { Level } from "../core/schema";
import type { Comfort } from "./comfort";
/**
 * Who am I, which mode, which workspaces (GET /api/internal/session). Ephemeral UI state lives in
 * Zustand (spec §10); domain state never does.
 */
import { create } from "zustand";
import { deviceLanguage, tellServer } from "../i18n";

export type InstallMode = "solo-open" | "solo-login" | "circle";

export interface DeviceInfo {
  id: string;
  name: string;
  mode: "shared" | "personal";
  ownerId: string | null;
  ownerName: string | null;
  idleMinutes: number | null;
  lent: boolean;
  lendIdleMinutes: number;
}

export interface Session {
  mode: InstallMode;
  circleName: string;
  principal: { id: string; kind: string; name: string; scopes: string[] } | null;
  profile?: {
    id: string;
    name: string;
    kind: string;
    isAdmin: boolean;
    color: string | null;
    lang: string | null;
    /** ADR 0019: what Settings and the task panel show, per person */
    levels?: { settings: Level; panel: Level; panelOwn?: Level | null };
    /** the person's comfort (ADR 0032): theme and the a11y switches, the same on every device */
    comfort?: Comfort;
  };
  workspaces?: { id: string; name: string; role: string; shared: boolean }[];
  /** circle mode (P4): this browser or installed app */
  device?: DeviceInfo | null;
  /** circle mode: this personal device is lent, so the picker shows */
  lending?: boolean;
  login?: { oidc: boolean; password: boolean };
  features?: { attachments: boolean; attachmentMaxMb: number; oidc: boolean; llm: boolean; tileUrl?: string };
}

interface SessionState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  workspaceId: string | null;
  load(): Promise<void>;
  selectWorkspace(id: string): void;
  logout(): Promise<void>;
}

const WS_KEY = "drang.device.workspace";

export const useSession = create<SessionState>((set, get) => ({
  session: null,
  loading: true,
  error: null,
  workspaceId: null,
  async load() {
    set({ loading: true, error: null });
    try {
      const res = await fetch("/api/internal/session", { credentials: "same-origin" });
      const session = (await res.json()) as Session;
      let workspaceId: string | null = null;
      try {
        workspaceId = localStorage.getItem(WS_KEY);
      } catch {
        /* no storage */
      }
      // `?workspace=<name or id>` selects once (kiosk setup links, spec §9.29) and is remembered
      const wanted = new URLSearchParams(location.search).get("workspace");
      const byParam = wanted && (session.workspaces ?? []).find((w) => w.id === wanted || w.name === wanted);
      if (byParam) {
        workspaceId = byParam.id;
        try {
          localStorage.setItem(WS_KEY, byParam.id);
        } catch {
          /* no storage */
        }
      }
      const ids = new Set((session.workspaces ?? []).map((w) => w.id));
      if (!workspaceId || !ids.has(workspaceId)) workspaceId = session.workspaces?.[0]?.id ?? null;
      set({ session, loading: false, workspaceId });
      // server-side messages follow the language this device shows (ADR 0017)
      const lang = deviceLanguage();
      if (session.principal?.kind === "browser" && session.profile && session.profile.lang !== lang)
        tellServer(lang);
    } catch {
      set({ loading: false, error: "error.generic" });
    }
  },
  selectWorkspace(id) {
    try {
      localStorage.setItem(WS_KEY, id);
    } catch {
      /* no storage */
    }
    set({ workspaceId: id });
  },
  async logout() {
    await fetch("/api/internal/logout", { method: "POST", credentials: "same-origin" });
    set({ session: { ...(get().session as Session), principal: null, workspaces: [] }, workspaceId: null });
  },
}));
